import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { 
  Shield, 
  Users, 
  BookOpen, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  ArrowLeft, 
  Home, 
  Flag, 
  Eye, 
  Search,
  Filter,
  MessageSquare,
  Ban,
  Play
} from "lucide-react";
import { deleteFromR2 } from "../../services/r2Service";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { Link } from "react-router-dom";

interface Course {
  id: string;
  title: string;
  description: string;
  instructorId: string;
  instructorName: string;
  published: boolean;
  createdAt: any;
  thumbnailUrl?: string;
  price: number;
  category?: string;
  flagged?: boolean;
  flagReason?: string;
}

interface User {
  uid: string;
  email: string;
  name: string;
  role: string;
  banned?: boolean;
  banReason?: string;
  reportCount?: number;
}

export default function ContentModeration() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "flagged" | "unpublished">("all");
  const [activeTab, setActiveTab] = useState<"courses" | "users">("courses");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      // Load courses
      const coursesSnap = await getDocs(collection(db, "courses"));
      const coursesData = coursesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Course[];
      setCourses(coursesData);

      // Load users
      const usersSnap = await getDocs(collection(db, "users"));
      const usersData = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as User[];
      setUsers(usersData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (courseId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, "courses", courseId), {
        published: !currentStatus
      });
      setCourses(courses.map(c => 
        c.id === courseId ? { ...c, published: !currentStatus } : c
      ));
    } catch (error) {
      console.error("Error updating course:", error);
      alert("เกิดข้อผิดพลาดในการอัปเดตคอร์ส");
    }
  };

  const handleFlagCourse = async (courseId: string, reason: string) => {
    try {
      await updateDoc(doc(db, "courses", courseId), {
        flagged: true,
        flagReason: reason,
        published: false
      });
      setCourses(courses.map(c => 
        c.id === courseId ? { ...c, flagged: true, flagReason: reason, published: false } : c
      ));
      alert("ทำการ Flag คอร์สเรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error flagging course:", error);
      alert("เกิดข้อผิดพลาด");
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะลบคอร์สนี้? การกระทำนี้ไม่สามารถย้อนกลับได้")) {
      return;
    }
    
    try {
      // First, get all media files for this course
      const mediaQuery = collection(db, "courses", courseId, "media");
      const mediaSnapshot = await getDocs(mediaQuery);
      
      // Delete all media files from R2
      const deletePromises = mediaSnapshot.docs.map(async (mediaDoc) => {
        const mediaData = mediaDoc.data();
        if (mediaData.url) {
          try {
            const url = new URL(mediaData.url);
            const key = url.pathname.substring(1);
            if (key.includes('uploads/')) {
              await deleteFromR2(key);
              console.log('✅ File deleted from R2:', key);
            }
          } catch (r2Error) {
            console.warn('Could not delete from R2:', r2Error);
          }
        }
      });
      
      // Wait for all R2 deletions to complete
      await Promise.all(deletePromises);
      
      // Delete the course document (this will also delete all media subcollection)
      await deleteDoc(doc(db, "courses", courseId));
      setCourses(courses.filter(c => c.id !== courseId));
      alert("ลบคอร์สและไฟล์ที่เกี่ยวข้องเรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error deleting course:", error);
      alert("เกิดข้อผิดพลาดในการลบคอร์ส");
    }
  };

  const handleBanUser = async (userId: string, reason: string) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        banned: true,
        banReason: reason
      });
      setUsers(users.map(u => 
        u.uid === userId ? { ...u, banned: true, banReason: reason } : u
      ));
      alert("แบนผู้ใช้เรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error banning user:", error);
      alert("เกิดข้อผิดพลาด");
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        banned: false,
        banReason: ""
      });
      setUsers(users.map(u => 
        u.uid === userId ? { ...u, banned: false, banReason: "" } : u
      ));
      alert("ยกเลิกการแบนเรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error unbanning user:", error);
      alert("เกิดข้อผิดพลาด");
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.instructorName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === "flagged") return matchesSearch && course.flagged;
    if (filterType === "unpublished") return matchesSearch && !course.published;
    return matchesSearch;
  });

  const filteredUsers = users.filter(user => {
    return user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.email?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    totalCourses: courses.length,
    publishedCourses: courses.filter(c => c.published).length,
    flaggedCourses: courses.filter(c => c.flagged).length,
    bannedUsers: users.filter(u => u.banned).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <Flag className="w-10 h-10 text-blue-600" />
                  ตรวจสอบเนื้อหา
                </h1>
                <p className="text-textLight">จัดการและตรวจสอบคอร์สและผู้ใช้</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">คอร์สทั้งหมด</p>
                  <p className="text-3xl font-bold text-textDark">{stats.totalCourses}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">เผยแพร่แล้ว</p>
                  <p className="text-3xl font-bold text-green-600">{stats.publishedCourses}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Flag className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ถูก Flag</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.flaggedCourses}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center">
                  <Ban className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้ใช้ถูกแบน</p>
                  <p className="text-3xl font-bold text-red-600">{stats.bannedUsers}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab("courses")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === "courses"
                  ? "bg-gradient-primary text-white shadow-lg"
                  : "bg-white text-textDark hover:bg-gray-50"
              }`}
            >
              <BookOpen className="w-5 h-5" />
              คอร์ส
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === "users"
                  ? "bg-gradient-primary text-white shadow-lg"
                  : "bg-white text-textDark hover:bg-gray-50"
              }`}
            >
              <Users className="w-5 h-5" />
              ผู้ใช้
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textLight" />
              <input
                type="text"
                placeholder={activeTab === "courses" ? "ค้นหาคอร์ส..." : "ค้นหาผู้ใช้..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
              />
            </div>
            {activeTab === "courses" && (
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="px-6 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none font-semibold"
              >
                <option value="all">ทั้งหมด</option>
                <option value="flagged">ถูก Flag</option>
                <option value="unpublished">ยังไม่เผยแพร่</option>
              </select>
            )}
          </div>
        </div>

        {/* Content */}
        {activeTab === "courses" ? (
          <Card>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-textDark mb-6">
                คอร์สทั้งหมด ({filteredCourses.length})
              </h2>

              {filteredCourses.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-textLight">ไม่พบคอร์ส</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCourses.map((course) => (
                    <div
                      key={course.id}
                      className={`border-2 rounded-xl p-6 transition-all ${
                        course.flagged
                          ? "border-blue-200 bg-blue-50"
                          : !course.published
                          ? "border-yellow-200 bg-yellow-50"
                          : "border-gray-200 bg-white hover:shadow-lg"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex gap-4 flex-1">
                          {course.thumbnailUrl && (
                            <img
                              src={course.thumbnailUrl}
                              alt={course.title}
                              className="w-32 h-20 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-xl font-bold text-textDark">{course.title}</h3>
                              {course.flagged && (
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                  <Flag className="w-3 h-3" />
                                  Flagged
                                </span>
                              )}
                              {!course.published && (
                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                                  ไม่เผยแพร่
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-textLight mb-2 line-clamp-2">
                              {course.description}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-textLight">
                              <span>ผู้สอน: {course.instructorName || "ไม่ระบุ"}</span>
                              <span>ราคา: ฿{course.price?.toLocaleString() || 0}</span>
                              {course.category && <span>หมวด: {course.category}</span>}
                            </div>
                            {course.flagged && course.flagReason && (
                              <div className="mt-2 p-2 bg-blue-100 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <strong>เหตุผล:</strong> {course.flagReason}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Link to={`/courses/${course.id}`} target="_blank">
                            <button className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all font-semibold">
                              <Eye className="w-4 h-4" />
                              ดู
                            </button>
                          </Link>
                          <button
                            onClick={() => handleTogglePublish(course.id, course.published)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-semibold ${
                              course.published
                                ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                                : "bg-green-100 text-green-700 hover:bg-green-200"
                            }`}
                          >
                            {course.published ? (
                              <>
                                <XCircle className="w-4 h-4" />
                                ซ่อน
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                เผยแพร่
                              </>
                            )}
                          </button>
                          {!course.flagged && (
                            <button
                              onClick={() => {
                                const reason = prompt("ระบุเหตุผลในการ Flag:");
                                if (reason) handleFlagCourse(course.id, reason);
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all font-semibold"
                            >
                              <Flag className="w-4 h-4" />
                              Flag
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCourse(course.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-semibold"
                          >
                            <Trash2 className="w-4 h-4" />
                            ลบ
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-textDark mb-6">
                ผู้ใช้ทั้งหมด ({filteredUsers.length})
              </h2>

              {filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-textLight">ไม่พบผู้ใช้</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-textDark">ชื่อ</th>
                        <th className="text-left py-3 px-4 font-semibold text-textDark">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-textDark">บทบาท</th>
                        <th className="text-left py-3 px-4 font-semibold text-textDark">สถานะ</th>
                        <th className="text-center py-3 px-4 font-semibold text-textDark">การกระทำ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.uid} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-semibold text-textDark">{user.name || "ไม่ระบุ"}</p>
                          </td>
                          <td className="py-3 px-4 text-textLight">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              user.role === "instructor"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-blue-100 text-blue-700"
                            }`}>
                              {user.role === "instructor" ? "ผู้สอน" : "ผู้เรียน"}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {user.banned ? (
                              <div>
                                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1 w-fit">
                                  <Ban className="w-3 h-3" />
                                  ถูกแบน
                                </span>
                                {user.banReason && (
                                  <p className="text-xs text-red-600 mt-1">{user.banReason}</p>
                                )}
                              </div>
                            ) : (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                                ปกติ
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {user.banned ? (
                              <button
                                onClick={() => handleUnbanUser(user.uid)}
                                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all font-semibold"
                              >
                                ยกเลิกแบน
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  const reason = prompt("ระบุเหตุผลในการแบน:");
                                  if (reason) handleBanUser(user.uid, reason);
                                }}
                                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-semibold"
                              >
                                แบน
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
